import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../shared/utils/api';
import StudyUnitCard from './StudyUnitCard';
import StudyUnitExpanded from './StudyUnitExpanded';
import SubtopicRow from './SubtopicRow';
import SubtopicDetailPanel from './SubtopicDetailPanel';
import TopicFilters from './TopicFilters';

const BAND = {
  weak: { ringColor: 'var(--sig-red)' },
  moderate: { ringColor: 'var(--sig-amber)' },
  strong: { ringColor: 'var(--sig-green)' },
  untouched: { ringColor: 'var(--text-muted)' },
};

function bandOf(value) {
  if (value == null) return 'untouched';
  const pct = value * 100;
  if (pct < 40) return 'weak';
  if (pct < 70) return 'moderate';
  return 'strong';
}

function aggregateConfidence(topics) {
  const vals = topics
    .map(t => t.raw_confidence ?? t.confidence)
    .filter(v => v != null);
  if (vals.length === 0) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

// Derive provider-level stats for a set of L3 topics by aggregating the
// topic-level provider_breakdown from each member's diagnostic endpoint.
// Skipped here to avoid N requests per SU — in a follow-up backend session
// add /api/study-units/<id>/provider-stats. For now we pass an empty object
// and the provider bar hides when empty.

export default function TopicsPage({ module, focusTopicId, onFocusConsumed }) {
  const [topics, setTopics] = useState([]);
  const [studyUnits, setStudyUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [activeSubject, setActiveSubject] = useState(null);
  const [view, setView] = useState('study_units');
  const [defaultViewLoaded, setDefaultViewLoaded] = useState(false);
  const [expandedUnitId, setExpandedUnitId] = useState(null);
  const [openTopicId, setOpenTopicId] = useState(null);
  const [engineReadiness, setEngineReadiness] = useState(null);

  const [searchParams] = useSearchParams();
  const urlFocusId = parseInt(searchParams.get('focus'), 10) || null;
  const effectiveFocusId = focusTopicId || urlFocusId;

  // Load the user's preferred view mode from settings
  useEffect(() => {
    api('/api/settings').then(s => {
      if (!s || s.error) return;
      if (s.topics_view === 'subtopics' || s.topics_view === 'study_units') {
        setView(s.topics_view);
      }
    }).catch(() => {}).finally(() => setDefaultViewLoaded(true));
  }, []);

  // Load topics and study units on module change
  useEffect(() => {
    setLoading(true);
    setExpandedUnitId(null);
    setOpenTopicId(null);
    setFilter('all');
    setSearch('');
    setActiveSubject(null);
    setEngineReadiness(null);
    Promise.all([
      api(`/api/topics/${module}`),
      api('/api/study-units'),
      api('/api/priority-engine/readiness'),
    ]).then(([tData, suData, engData]) => {
      setTopics(Array.isArray(tData) ? tData : []);
      setStudyUnits(Array.isArray(suData) ? suData : []);
      setEngineReadiness(engData && !engData.error ? engData : null);
      setLoading(false);
    }).catch(() => {
      setTopics([]);
      setStudyUnits([]);
      setEngineReadiness(null);
      setLoading(false);
    });
  }, [module]);

  // When focus requested, open that topic
  useEffect(() => {
    if (!effectiveFocusId || loading) return;
    const target = topics.find(t => t.id === effectiveFocusId);
    if (!target) return;
    if (view === 'study_units') {
      const unit = studyUnits.find(su =>
        su.module === module && (su.member_topic_ids || []).includes(target.id)
      );
      if (unit) setExpandedUnitId(unit.id);
    }
    setOpenTopicId(target.id);
    // Scroll to topic after React re-renders the expanded content (200ms covers the render cycle)
    const scrollTimer = setTimeout(() => {
      const el = document.getElementById(`topic-row-${target.id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
    const timer = setTimeout(() => onFocusConsumed && onFocusConsumed(), 1500);
    return () => { clearTimeout(timer); clearTimeout(scrollTimer); };
  }, [effectiveFocusId, loading, topics, studyUnits, view, module, onFocusConsumed]);

  const handleSaveTopic = useCallback((id, updates) => {
    setTopics(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
  }, []);

  const handleViewChange = (next) => {
    setView(next);
    setExpandedUnitId(null);
    setOpenTopicId(null);
    api('/api/settings', {
      method: 'PATCH',
      body: JSON.stringify({ topics_view: next }),
    }).catch(() => {});
  };

  const moduleStudyUnits = useMemo(
    () => studyUnits.filter(su => su.module === module),
    [studyUnits, module]
  );

  const topicById = useMemo(() => {
    const m = new Map();
    topics.forEach(t => m.set(t.id, t));
    return m;
  }, [topics]);

  const engineSuById = useMemo(() => {
    if (!engineReadiness?.study_units) return {};
    return engineReadiness.study_units;
  }, [engineReadiness]);

  const engineTopicById = useMemo(() => {
    if (!engineReadiness?.topics) return {};
    return engineReadiness.topics;
  }, [engineReadiness]);

  // Subjects list from topics
  const subjects = useMemo(() => {
    const set = new Set();
    topics.forEach(t => {
      const s = t.main_topic || t.subject_area || t.topic;
      if (s) set.add(s);
    });
    return Array.from(set);
  }, [topics]);

  // Filter topics by search + subject
  const filteredTopics = useMemo(() => {
    const q = search.trim().toLowerCase();
    return topics.filter(t => {
      if (activeSubject) {
        const s = t.main_topic || t.subject_area || t.topic;
        if (s !== activeSubject) return false;
      }
      if (q) {
        const blob = [
          t.subtopic, t.display_name, t.topic, t.main_topic,
          t.general_topic, t.sub_subtopic, t.detail,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [topics, search, activeSubject]);

  // Study unit derived state
  const decoratedUnits = useMemo(() => {
    const filteredIds = new Set(filteredTopics.map(t => t.id));
    const q = search.trim().toLowerCase();
    return moduleStudyUnits.map(su => {
      const memberIds = su.member_topic_ids || [];
      const allMembers = memberIds
        .map(id => topicById.get(id))
        .filter(Boolean);
      const unitNameMatch = q && (
        (su.name || '').toLowerCase().includes(q) ||
        (su.subject_area || '').toLowerCase().includes(q)
      );
      const visibleMembers = unitNameMatch
        ? allMembers
        : allMembers.filter(t => filteredIds.has(t.id));
      const suEngine = engineSuById[String(su.id)];
      const readiness = suEngine?.readiness != null
        ? suEngine.readiness / 100
        : aggregateConfidence(allMembers);
      const ratedMembers = allMembers.filter(t => t.confidence != null).length;
      const allStrong = allMembers.length > 0
        && allMembers.every(t => {
          const eng = engineTopicById[String(t.id)];
          const val = eng?.readiness != null ? eng.readiness / 100 : (t.raw_confidence ?? t.confidence ?? 0);
          return val >= 0.7;
        });
      // SU trend: use engine trend if available, else majority direction across members (tie → stable).
      const engineTrend = suEngine?.trend;
      let trend;
      if (engineTrend === 'improving') trend = 'up';
      else if (engineTrend === 'declining') trend = 'down';
      else if (engineTrend != null) trend = 'stable';
      else {
        const tally = { up: 0, down: 0, stable: 0 };
        allMembers.forEach(m => {
          const d = m.trend_direction;
          if (d === 'up' || d === 'down' || d === 'stable') tally[d] += 1;
        });
        trend = 'stable';
        if (tally.up > tally.down && tally.up > tally.stable) trend = 'up';
        else if (tally.down > tally.up && tally.down > tally.stable) trend = 'down';
      }
      return {
        ...su,
        allMembers,
        visibleMembers,
        readiness,
        ratedMembers,
        totalMembers: allMembers.length,
        band: bandOf(readiness),
        allStrong,
        trend,
        sourceLabel: suEngine?.primary_source_label ?? null,
      };
    });
  }, [moduleStudyUnits, topicById, filteredTopics, search, engineSuById, engineTopicById]);

  // Counts for filter pills
  const counts = useMemo(() => {
    if (view === 'study_units') {
      const c = { all: 0, weak: 0, untouched: 0, moderate: 0, strong: 0 };
      decoratedUnits.forEach(u => {
        if (u.allMembers.length === 0) return;
        c.all += 1;
        c[u.band] += 1;
      });
      return c;
    }
    const c = { all: 0, weak: 0, untouched: 0, moderate: 0, strong: 0 };
    filteredTopics.forEach(t => {
      c.all += 1;
      const eng = engineTopicById[String(t.id)];
      const val = eng?.readiness != null ? eng.readiness / 100 : (t.raw_confidence ?? t.confidence);
      c[bandOf(val)] += 1;
    });
    return c;
  }, [view, decoratedUnits, filteredTopics, engineTopicById]);

  // Apply the filter pill
  const filteredUnits = useMemo(() => {
    if (view !== 'study_units') return [];
    if (filter === 'all') return decoratedUnits.filter(u => u.visibleMembers.length > 0);
    return decoratedUnits.filter(u => u.visibleMembers.length > 0 && u.band === filter);
  }, [view, filter, decoratedUnits]);

  const filteredSubtopics = useMemo(() => {
    if (view !== 'subtopics') return [];
    if (filter === 'all') return filteredTopics;
    return filteredTopics.filter(t => {
      const eng = engineTopicById[String(t.id)];
      const val = eng?.readiness != null ? eng.readiness / 100 : (t.raw_confidence ?? t.confidence);
      return bandOf(val) === filter;
    });
  }, [view, filter, filteredTopics, engineTopicById]);

  // Group helpers — subject → general_topic → items
  const groupedUnits = useMemo(() => {
    const bySubject = new Map();
    filteredUnits.forEach(u => {
      const s = u.subject_area || 'Other';
      if (!bySubject.has(s)) bySubject.set(s, []);
      bySubject.get(s).push(u);
    });
    return bySubject;
  }, [filteredUnits]);

  const groupedSubtopics = useMemo(() => {
    const bySubject = new Map();
    filteredSubtopics.forEach(t => {
      const s = t.main_topic || t.subject_area || 'Other';
      if (!bySubject.has(s)) bySubject.set(s, new Map());
      const gtMap = bySubject.get(s);
      const gt = t.general_topic || 'General';
      if (!gtMap.has(gt)) gtMap.set(gt, []);
      gtMap.get(gt).push(t);
    });
    return bySubject;
  }, [filteredSubtopics]);

  // Tally subject rated counts for headers
  const subjectCounts = useMemo(() => {
    const m = new Map();
    topics.forEach(t => {
      const s = t.main_topic || t.subject_area || 'Other';
      if (!m.has(s)) m.set(s, { total: 0, rated: 0 });
      const entry = m.get(s);
      entry.total += 1;
      if (t.confidence != null) entry.rated += 1;
    });
    return m;
  }, [topics]);

  if (loading || !defaultViewLoaded) {
    return (
      <div className="topics-v3">
        <div className="tv3-empty">Loading {module}…</div>
      </div>
    );
  }

  const moduleLabel = module === 'FLK1' ? 'FLK1' : 'FLK2';

  return (
    <div className="topics-v3">
      <h1 className="tv3-title">{moduleLabel} topics</h1>

      <TopicFilters
        filter={filter}
        onFilterChange={setFilter}
        counts={counts}
        view={view}
        onViewChange={handleViewChange}
        search={search}
        onSearchChange={setSearch}
        subjects={subjects}
        activeSubject={activeSubject}
        onSubjectChange={setActiveSubject}
        moduleLabel={moduleLabel}
      />

      {view === 'study_units' && <StudyUnitsView
        groupedUnits={groupedUnits}
        subjectCounts={subjectCounts}
        expandedUnitId={expandedUnitId}
        onToggleUnit={id => {
          setExpandedUnitId(prev => prev === id ? null : id);
          setOpenTopicId(null);
        }}
        openTopicId={openTopicId}
        onSelectTopic={setOpenTopicId}
        onSaveTopic={handleSaveTopic}
        engineTopicById={engineTopicById}
      />}

      {view === 'subtopics' && <SubtopicsView
        groupedSubtopics={groupedSubtopics}
        subjectCounts={subjectCounts}
        openTopicId={openTopicId}
        onToggleTopic={id => setOpenTopicId(prev => prev === id ? null : id)}
        onSaveTopic={handleSaveTopic}
        engineTopicById={engineTopicById}
      />}

      {view === 'study_units' && groupedUnits.size === 0 && (
        <div className="tv3-empty">No study units match this filter.</div>
      )}
      {view === 'subtopics' && groupedSubtopics.size === 0 && (
        <div className="tv3-empty">No subtopics match this filter.</div>
      )}
    </div>
  );
}

function StudyUnitsView({
  groupedUnits, subjectCounts, expandedUnitId, onToggleUnit, openTopicId, onSelectTopic, onSaveTopic, engineTopicById,
}) {
  return (
    <>
      {Array.from(groupedUnits.entries()).map(([subject, units]) => {
        const subjectInfo = subjectCounts.get(subject) || { total: 0, rated: 0 };
        // Group units under their general_topic
        const byGT = new Map();
        units.forEach(u => {
          const gt = u.general_topic || 'General';
          if (!byGT.has(gt)) byGT.set(gt, []);
          byGT.get(gt).push(u);
        });
        return (
          <div key={subject}>
            <div className="tv3-subject-header">
              <span className="tv3-subject-name">{subject}</span>
              <span className="tv3-subject-meta">
                {subjectInfo.rated} of {subjectInfo.total} subtopics rated
              </span>
            </div>
            {Array.from(byGT.entries()).map(([gt, gtUnits]) => {
              const avg = aggregateConfidence(
                gtUnits.flatMap(u => u.allMembers)
              );
              const band = bandOf(avg);
              return (
                <div key={gt}>
                  <div className="tv3-group-header">
                    <span className="tv3-group-name">{gt}</span>
                    <span className="tv3-group-count">
                      <span
                        className="tv3-group-dot"
                        style={{ background: BAND[band].ringColor }}
                      />
                      {gtUnits.length} study unit{gtUnits.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  {gtUnits.map(u => (
                    <React.Fragment key={u.id}>
                      <StudyUnitCard
                        unit={u}
                        readiness={u.readiness}
                        trend={u.trend}
                        totalMembers={u.totalMembers}
                        ratedMembers={u.ratedMembers}
                        open={expandedUnitId === u.id}
                        allStrong={u.allStrong}
                        sourceLabel={u.sourceLabel}
                        onClick={() => onToggleUnit(u.id)}
                      />
                      {expandedUnitId === u.id && (
                        <StudyUnitExpanded
                          studyUnitId={u.id}
                          members={u.allMembers}
                          engineTopicById={engineTopicById}
                          selectedTopicId={openTopicId}
                          onSelectTopic={onSelectTopic}
                          onSaveTopic={onSaveTopic}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
}

function SubtopicsView({ groupedSubtopics, subjectCounts, openTopicId, onToggleTopic, onSaveTopic, engineTopicById }) {
  return (
    <>
      {Array.from(groupedSubtopics.entries()).map(([subject, gtMap]) => {
        const subjectInfo = subjectCounts.get(subject) || { total: 0, rated: 0 };
        return (
          <div key={subject}>
            <div className="tv3-subject-header">
              <span className="tv3-subject-name">{subject}</span>
              <span className="tv3-subject-meta">
                {subjectInfo.rated} of {subjectInfo.total} subtopics rated
              </span>
            </div>
            {Array.from(gtMap.entries()).map(([gt, items]) => {
              const avg = aggregateConfidence(items);
              const band = bandOf(avg);
              return (
                <div key={gt}>
                  <div className="tv3-group-header">
                    <span className="tv3-group-name">{gt}</span>
                    <span className="tv3-group-count">
                      <span
                        className="tv3-group-dot"
                        style={{ background: BAND[band].ringColor }}
                      />
                      {items.length} subtopic{items.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  {items.map(t => {
                    const eng = engineTopicById?.[String(t.id)];
                    const enrichedTopic = eng ? {
                      ...t,
                      engine_readiness: eng.readiness != null ? eng.readiness / 100 : undefined,
                      engine_label: eng.primary_source_label,
                    } : t;
                    return (
                      <React.Fragment key={t.id}>
                        <SubtopicRow
                          topic={enrichedTopic}
                          variant="ds"
                          selected={openTopicId === t.id}
                          trend={t.trend_direction}
                          onClick={() => onToggleTopic(t.id)}
                        />
                        {openTopicId === t.id && (
                          <div className="tv3-subtopics-detail-wrapper">
                            <SubtopicDetailPanel topic={t} onSave={onSaveTopic} />
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
}
